import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "images" }

import CurrentLanguage from '@/components/CurrentLanguage.vue';
import MediaField from '@/components/media/MediaField.vue';
import MediaItem from '@/components/media/MediaItem.vue';
import { Media, defaultMedia } from '@/models/media.model';
import { useEvents, Event } from '@/services/events.composable';
import mediaService from '@/services/media.service';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { NEW_ID } from '@/util/constants';
import { computed, onBeforeUnmount, onMounted, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';


export default /*@__PURE__*/_defineComponent({
  __name: 'MediaForm',
  props: {
  data: {
    type: Object as PropType<{ media?: Media }>,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const store = useStore<ShiftCMSState>();
const events = useEvents();
const route = useRoute();

const { uuid } = route.params; // for external editors
const { t } = useI18n();

const mediaSearch = ref<Media[]>([]);

const media = ref<Media>({ ...defaultMedia }); // upload media
const media2 = ref<Media>({ ...(props.data.media || defaultMedia) }); // edit media

const title = computed<string>(() =>
  media2.value.id === NEW_ID ? t('media.add') : t('media.edit'),
);
const action = computed<string>(() =>
  media2.value.id === NEW_ID ? t('media.add') : t('media.save'),
);
const tab = ref<string>('upload');
const search = ref<string>('');
const uploadValid = ref<boolean>(false);
const editValid = ref<boolean>(false);

const uploadMedia = async () => {
  if (uploadValid.value) {
    const result = await store.dispatch(Actions.UploadMedia, {
      uuid,
      media: media.value,
    });

    if (result) {
      events.emit(Event.UseMedia, result);
      store.dispatch(Actions.ShowNotification, {
        message: 'Uploaded media',
        type: 'success',
      });
    } else {
      store.dispatch(Actions.ShowNotification, {
        message: 'Failed to upload media',
        type: 'error',
      });
    }
  }
};
const addMedia = async () => {
  if (editValid.value) {
    if (media2.value.id !== NEW_ID) {
      const result = await store.dispatch(Actions.UpdateMedia, {
        uuid,
        media: media2.value,
      });

      if (result) {
        store.dispatch(Actions.ShowNotification, {
          message: 'Saved media',
          type: 'success',
        });
        events.emit(Event.UpdateMedia, result);
      } else {
        store.dispatch(Actions.ShowNotification, {
          message: 'Failed to save media',
          type: 'error',
        });
      }
    } else {
      const result = await store.dispatch(Actions.AddMedia, {
        uuid,
        media: media2.value,
      });

      if (result) {
        store.dispatch(Actions.ShowNotification, {
          message: 'Saved media',
          type: 'success',
        });
        events.emit(Event.UseMedia, result);
      } else {
        store.dispatch(Actions.ShowNotification, {
          message: 'Failed to add media',
          type: 'error',
        });
      }
    }
  }
};

const searchMedia = async () => {
  if (search.value) {
    const result = await store.dispatch(Actions.SearchMedia, search.value);

    mediaSearch.value = result.data.reduce((acc: Media[], v: Media) => {
      if (acc.map((a) => a.file?.uuid).includes(v.file?.uuid)) {
        return acc;
      }
      return [...acc, v];
    }, []);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Please provide a value for searching',
      type: 'info',
    });
  }
};

const useMedia = (m: Media) => {
  Object.assign(media2.value, {
    ...defaultMedia,
    description: m.description,
    file: m.file,
  });

  editMedia(media2.value);
};
const editMedia = (m: Media) => {
  Object.assign(media2.value, m);
  tab.value = 'edit';
};
const cancelEdit = () => {
  store.commit(Mutations.HideSubDrawer);
  store.commit(Mutations.HideDrawer);
};

const updateLanguage = async () => {
  if (media2.value.id === NEW_ID) {
    return;
  }
  const result = await mediaService.get(media2.value.id);

  if (result.status) {
    Object.assign(media2.value, { ...result.data });
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  if (props.data && props.data.media) {
    editMedia(props.data.media);
  } else {
    Object.assign(media.value, { ...defaultMedia });
    Object.assign(media2.value, { ...defaultMedia });
  }
  updateLanguage();
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

return (_ctx: any,_cache: any) => {
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_tabs_window_item = _resolveComponent("v-tabs-window-item")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_tabs_window = _resolveComponent("v-tabs-window")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (media2.value.id === _unref(NEW_ID))
      ? (_openBlock(), _createBlock(_component_v_alert, {
          key: 0,
          color: "info",
          class: "ma-4 mt-8 pa-4"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('media.uploadOrSearch')), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_component_v_tabs, {
      modelValue: tab.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tab).value = $event)),
      "fixed-tabs": ""
    }, {
      default: _withCtx(() => [
        (media2.value.id === _unref(NEW_ID))
          ? (_openBlock(), _createBlock(_component_v_tab, {
              key: 0,
              value: "upload"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('media.upload')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (media2.value.id !== _unref(NEW_ID))
          ? (_openBlock(), _createBlock(_component_v_tab, {
              key: 1,
              value: "edit"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('media.edit')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (media2.value.id === _unref(NEW_ID))
          ? (_openBlock(), _createBlock(_component_v_tab, {
              key: 2,
              value: "search"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('media.search')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_tabs_window, {
      modelValue: tab.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((tab).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_tabs_window_item, { value: "upload" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_form, {
              modelValue: uploadValid.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((uploadValid).value = $event)),
              onSubmit: _withModifiers(uploadMedia, ["prevent"])
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_container, null, {
                  default: _withCtx(() => [
                    _createElementVNode("h2", null, [
                      _createTextVNode(_toDisplayString(_ctx.$t('media.uploadNew')) + " ", 1),
                      _createVNode(CurrentLanguage)
                    ]),
                    _createVNode(MediaField, {
                      modelValue: media.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((media).value = $event))
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_v_row, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_file_input, {
                              label: "File input",
                              modelValue: media.value.file,
                              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((media.value.file) = $event)),
                              required: ""
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_row, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_col, { col: "2" }),
                        _createVNode(_component_v_col, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_btn, {
                              class: "mt-2",
                              type: "submit",
                              color: "success",
                              block: ""
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t('media.upload')), 1)
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_col, { col: "2" })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_component_v_tabs_window_item, { value: "edit" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_container, null, {
              default: _withCtx(() => [
                _createElementVNode("h2", null, [
                  _createTextVNode(_toDisplayString(title.value) + " ", 1),
                  _createVNode(CurrentLanguage, { buttons: "" })
                ]),
                _createVNode(_component_v_form, {
                  modelValue: editValid.value,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((editValid).value = $event)),
                  onSubmit: _withModifiers(addMedia, ["prevent"])
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_container, null, {
                      default: _withCtx(() => [
                        _createVNode(MediaField, {
                          modelValue: media2.value,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((media2).value = $event))
                        }, null, 8, ["modelValue"]),
                        (media2.value.file)
                          ? (_openBlock(), _createBlock(MediaItem, {
                              key: 0,
                              media: media2.value,
                              showOptions: []
                            }, null, 8, ["media"]))
                          : _createCommentVNode("", true),
                        _createVNode(_component_v_row, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_col, { col: "2" }),
                            _createVNode(_component_v_col, null, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_btn, {
                                  class: "mt-2",
                                  type: "button",
                                  onClick: _withModifiers(cancelEdit, ["prevent"]),
                                  block: ""
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_unref(t)('media.cancel')), 1)
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_col, null, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_btn, {
                                  class: "mt-2",
                                  type: "submit",
                                  block: ""
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(action.value), 1)
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_col, { col: "2" })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_v_tabs_window_item, { value: "search" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_container, null, {
              default: _withCtx(() => [
                _createElementVNode("h2", null, _toDisplayString(_ctx.$t('media.search')), 1),
                _createVNode(_component_v_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, { cols: "10" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: search.value,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((search).value = $event)),
                          label: "Search"
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, {
                      cols: "2",
                      class: "mt-2"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, { onClick: searchMedia }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t('media.search')), 1)
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, null, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mediaSearch.value, (m) => {
                            return (_openBlock(), _createBlock(MediaItem, {
                              media: m,
                              key: m.id,
                              onUse: useMedia
                            }, null, 8, ["media"]))
                          }), 128))
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})